'use client'

import { type ComponentProps, type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { Dialog, DialogContent, DialogTitle } from 'base-ui'

import { DrawerTransition } from '../../../drawer-transition'
import { useGlobalConfigContext } from '../../../global-config'
import { ProductPrescriptionDialog } from '../../../product'
import { TENANT } from '../../../tenant'

import { DynamicProductPrescriptionDrawerContent } from './components/DynamicProductPrescriptionDrawerContent'
import { type ProductPrescriptionDrawerControls } from './useProductPrescriptionDrawerControls'

import styles from './ProductPrescriptionDrawer.module.css'

const PRODUCT_PRESCRIPTION_DRAWER_TITLE_ID = 'rx-pdp-app-drawer-title'

type ProductPrescriptionDrawerProps = {
  controls: ProductPrescriptionDrawerControls
}

export const ProductPrescriptionDrawer: FC<ProductPrescriptionDrawerProps> = ({
  controls,
}) => {
  const globalConfig = useGlobalConfigContext()

  if (globalConfig.tenant !== TENANT.COM) {
    return (
      <ProductPrescriptionDialog
        isOpen={controls.isOpen}
        onClose={controls.handleClose}
      />
    )
  }

  const drawerTransitionProps = {
    in: controls.isOpen,
  } satisfies Pick<ComponentProps<typeof DrawerTransition>, 'in'>

  return (
    <Dialog
      className="flex items-end justify-end"
      closeAfterTransition
      onClose={controls.handleClose}
      onTransitionEnter={controls.handleEnter}
      open={controls.isOpen}
      slotProps={{
        dialog: {
          'aria-labelledby': PRODUCT_PRESCRIPTION_DRAWER_TITLE_ID,
          className: styles['product-prescription-drawer'],
          ...drawerTransitionProps,
        },
      }}
      slots={{ dialog: DrawerTransition }}
    >
      <DialogTitle
        className="shrink-0 bg-light-primary-medium px-6 py-4"
        onClose={controls.handleClose}
        slotProps={{
          heading: {
            className:
              'text-l font-medium text-dark-primary-max tablet:text-xl',
            id: PRODUCT_PRESCRIPTION_DRAWER_TITLE_ID,
          },
        }}
      >
        <FormattedMessage id="rxPdpAppDrawer.title" />
      </DialogTitle>
      <DialogContent className="flex-1 overflow-y-auto p-0">
        <DynamicProductPrescriptionDrawerContent
          onCartFlowStarted={controls.handleClose}
        />
      </DialogContent>
    </Dialog>
  )
}
